import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const STAGE_TONE = {
  New: "slate", Screening: "blue", "Recruiter Screened": "blue", Assessment: "violet",
  Shortlisted: "cyan", "Client Submitted": "amber", "Client Review": "amber",
  "Interview 1": "violet", "Interview 2": "violet", "Final Interview": "violet",
  Selected: "emerald", Offer: "emerald", "Offer Accepted": "emerald", Joined: "emerald",
  Rejected: "rose", "On Hold": "slate",
  Open: "emerald", Draft: "slate", Closed: "slate", Filled: "blue", Cancelled: "rose",
  Active: "emerald", Placed: "blue", Blacklisted: "rose", "Not Interested": "slate",
  Archived: "slate", Critical: "rose", High: "amber", Medium: "blue", Low: "slate",
  Scheduled: "blue", Completed: "emerald", "No Show": "rose", Rescheduled: "amber",
  "Offer Draft": "slate", "Offer Sent": "amber", "Offer Declined": "rose",
  "Joining Confirmed": "cyan", Dropout: "rose", "Strong Hire": "emerald", Hire: "cyan",
  Maybe: "amber", "No Hire": "rose", "Renewal Due": "amber",
  "In Progress": "blue", Overdue: "rose",
};

const TONES = {
  slate: "bg-slate-100 text-slate-600 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

export const StatusBadge = ({ value, className, testid }) => {
  if (!value) return <span className="text-xs text-slate-500">—</span>;
  const tone = TONES[STAGE_TONE[value] || "slate"];
  return (
    <span
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap",
        tone, className
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {value}
    </span>
  );
};

export const PageHeader = ({ title, subtitle, actions, meta }) => (
  <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-end md:justify-between">
    <div className="min-w-0">
      {meta && <p className="label-xs mb-2">{meta}</p>}
      <h1 className="text-2xl font-medium tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-sm text-slate-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const KpiCard = ({ label, value, sub, icon: Icon, tone = "blue", onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={cn(
      "surface surface-hover group relative overflow-hidden rounded-md p-5 text-left",
      onClick ? "cursor-pointer" : "cursor-default"
    )}
  >
    <div className="flex items-start justify-between gap-3">
      <p className="label-xs leading-relaxed">{label}</p>
      {Icon && (
        <span className={cn("rounded-md border p-1.5", TONES[tone])}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
    <p className="font-mono-num mt-4 text-3xl tracking-tighter text-slate-900">{value ?? "—"}</p>
    {sub && <p className="mt-1.5 text-xs text-slate-500">{sub}</p>}
  </button>
);

export const EmptyState = ({ icon: Icon, title, description, action, testid }) => (
  <div
    data-testid={testid}
    className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-200 px-6 py-16 text-center"
  >
    {Icon && (
      <span className="mb-4 rounded-full border border-slate-200 bg-white p-3">
        <Icon className="h-5 w-5 text-slate-500" />
      </span>
    )}
    <h3 className="text-base font-medium text-slate-800">{title}</h3>
    {description && <p className="mt-2 max-w-md text-sm text-slate-500">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const TableSkeleton = ({ rows = 6, cols = 5 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {Array.from({ length: cols }).map((__, j) => (
          <Skeleton key={j} className="h-9 bg-slate-100" />
        ))}
      </div>
    ))}
  </div>
);

export const CardSkeleton = ({ count = 6 }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} className="h-40 rounded-md bg-slate-100" />
    ))}
  </div>
);

export const scoreTone = (score) =>
  score >= 80 ? "#10b981" : score >= 65 ? "#38bdf8" : score >= 45 ? "#f59e0b" : "#ef4444";

export const MatchRing = ({ score, size = 76, label = "MATCH", testid }) => {
  const safe = Math.max(0, Math.min(100, score ?? 0));
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative inline-flex items-center justify-center" data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#e2e8f0" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={scoreTone(safe)} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={c - (c * safe) / 100} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 700ms cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono-num text-lg font-medium leading-none text-slate-900">{safe}%</span>
        <span className="mt-0.5 text-[8px] tracking-[0.16em] text-slate-500">{label}</span>
      </div>
    </div>
  );
};

export const ScoreBar = ({ label, value, hint }) => (
  <div>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs capitalize text-slate-500">{label.replace(/_/g, " ")}</span>
      <span className="font-mono-num text-xs text-slate-600">
        {value === null || value === undefined ? "Not available" : `${value}%`}
      </span>
    </div>
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
      <div
        className="h-full rounded-full"
        style={{
          width: `${value ?? 0}%`,
          background: value === null || value === undefined ? "#cbd5e1" : scoreTone(value),
          transition: "width 600ms cubic-bezier(0.16,1,0.3,1)",
        }}
      />
    </div>
    {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
  </div>
);

export const AiNotice = ({ text = "AI-generated suggestion based on stored profile data. Recruiter judgement required." }) => (
  <p className="mt-3 flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] leading-relaxed text-violet-700/80">
    <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400" />
    {text}
  </p>
);

export const Field = ({ label, value, mono }) => (
  <div>
    <p className="label-xs">{label}</p>
    <p className={cn("mt-1 text-sm text-slate-800", mono && "font-mono-num")}>
      {value === null || value === undefined || value === "" ? (
        <span className="text-slate-400">Not available</span>
      ) : (
        value
      )}
    </p>
  </div>
);

export const SkillChip = ({ skill, tone = "slate" }) => (
  <span
    className={cn(
      "rounded border px-2 py-0.5 text-[11px]",
      tone === "matched"
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : tone === "missing"
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : "border-slate-200 bg-slate-100 text-slate-600"
    )}
  >
    {skill}
  </span>
);

export const Hint = ({ children, text }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-xs border-slate-200 bg-slate-100 text-xs text-slate-600">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export { Badge };
